// The pricing engine page's results (plan ppe-simplify-epps.md, section 6.0 and
// work package 6): one engine response per loan term, turned into the
// products the table shows, grouped by term and ordered at the target
// (D29; by rate since 11-04).
// Pure functions only; the components in this folder render what they return.
import * as com from "../../../../Common";
import {
  getMaxPriceBreakdown,
  getNetPricingBreakdown,
} from "../../../MortgageCalculators/priceBreakdown";
import { ARM_TERM_OPTIONS, restoreArmTerm } from "../../armTerms";
import { calculateLoanMetrics, getCollateralValue } from "../../loanMetrics";
import { TARGET_BASIS_COST, TARGET_BASIS_PRICE, searchTargetOf, targetPriceBasisOf } from "../../pricingRequest";
import {
  buildIneligibleByLender,
  isRateSheetStale,
  loanTermLabel,
  rateSheetDate,
  zeroResultSentence,
} from "../../ineligibleReasons";

export const FIXED_TERMS = ["fixed40", "fixed30", "fixed25", "fixed20", "fixed15", "fixed10"];
export const ARM_TERMS = ARM_TERM_OPTIONS.map((option) => option.value);
export const HELOC_TERMS = ["fixed30", "fixed25", "fixed15"];
const TERM_ORDER = [...FIXED_TERMS, ...ARM_TERMS];

export const termLabel = (term) => loanTermLabel(term);

export function termGroupsFor(loanProgram) {
  if (loanProgram === "heloc") return [["Fixed", HELOC_TERMS]];
  return [["Fixed", FIXED_TERMS], ["ARM", ARM_TERMS]];
}

const termRank = (term) => {
  const i = TERM_ORDER.indexOf(term);
  return i === -1 ? TERM_ORDER.length : i;
};

export const sortTerms = (terms) =>
  [...new Set(terms)].sort((a, b) => termRank(a) - termRank(b));

// The terms a search asked for. The new form sends selectedLoanTerms (work
// package 5); older bodies and saved searches only have loanTerm.
export function termsOfSearch(searchParams) {
  const sp = searchParams || {};
  const list = Array.isArray(sp.selectedLoanTerms) && sp.selectedLoanTerms.length > 0
    ? sp.selectedLoanTerms
    : [sp.loanTerm || "fixed30"];
  return sortTerms(list.filter(Boolean).map(restoreArmTerm));
}

const positive = (...values) => values.map(Number).find((v) => Number.isFinite(v) && v > 0);

// Dollar amounts in the table are points x this amount: the draw for a
// HELOC, the subject loan otherwise (same rule as the page used before).
export function loanAmountOf(searchParams) {
  const sp = searchParams || {};
  const wire = sp.pricingRequest || {};
  const program = String(wire.loanProgram || sp.loanProgram || "").toLowerCase();
  if (program === "heloc") {
    return positive(wire.drawAmount, sp.drawAmount, wire.baseLoanAmount, sp.baseLoanAmount, wire.lineAmount, sp.lineAmount, sp.loanAmount) || 0;
  }
  if (sp.purpose === "Purchase" && program !== "secondmortgage" && Number(sp.propertyValue) > 0 && sp.downPayment !== undefined && sp.downPayment !== null) {
    return Number(sp.propertyValue) - Number(sp.downPayment);
  }
  return Number(wire.baseLoanAmount ?? sp.loanAmount ?? sp.baseLoanAmount) || 0;
}

// The subject loan amount with the filter bar's values as typed (the same
// amount filterBarBody sends; a HELOC's draw is not in the bar, so the
// search's), for the "≈ $4,000 cost to borrower" line under the target.
export function barLoanAmount(searchParams, values) {
  const sp = searchParams || {};
  const v = values || {};
  if (sp.loanProgram === "heloc") return loanAmountOf(sp);
  if (sp.purpose === "Purchase" && !SECOND_LIEN.includes(sp.loanProgram)) {
    return (Number(v.propertyValue) || 0) - (Number(v.downPayment) || 0);
  }
  return Number(v.loanAmount) || 0;
}

// Income was entered (work package 5 leaves monthlyIncome empty when the loan
// officer did not type one; the calculator then reports dtiChecked=false).
export function incomeEntered(searchParams) {
  const sp = searchParams || {};
  const wire = sp.pricingRequest || {};
  return Number(wire.monthlyIncome ?? sp.monthlyIncome) > 0;
}

// --- the filter bar's target ------------------------------------------------

// The bar's Target at a new search: by price or by rate as the search was
// (pricingRequest.searchTargetOf). Both boxes keep a value so switching back
// and forth does not lose it; a price outside 50-150 (an old search by rate
// with the rate in targetPrice) starts at 100. basis: what the price is
// compared with (Price or Cost to borrower), kept by a search by rate too so
// switching back to Price finds it.
export function initialBarTarget(searchParams) {
  const sp = searchParams || {};
  const target = searchTargetOf(sp);
  const price = Number(sp.targetPrice);
  const usablePrice = Number.isFinite(price) && price >= 50 && price <= 150 && sp.targetPrice !== null && sp.targetPrice !== "";
  return {
    by: target.by,
    price: target.by === "price" && usablePrice ? target.value : (usablePrice ? price : 100),
    rate: target.by === "rate" ? target.value : null,
    basis: targetPriceBasisOf(sp),
  };
}

// The rate the bar's Rate box starts at when the officer switches to Rate
// with no rate typed: the lowest rate the shown products have at the current
// target price (the top row by price, on the price's basis), so the table
// does not jump.
export function startingTargetRate(products, targetPrice, basis) {
  const rates = (products || [])
    .map((p) => targetRow(p, { by: "price", value: targetPrice, basis }))
    .filter((row) => row && Number.isFinite(row.rate))
    .map((row) => row.rate);
  return rates.length ? Math.min(...rates) : null;
}

// --- the filter bar's re-quote body ----------------------------------------

const SECOND_LIEN = ["heloc", "secondMortgage"];

// The form body with the filter bar's property value, down payment / loan
// amount, compensation type and target (by price or by rate, when given), in
// the shape formatPpeSearchParams reads.
// Every ratio is derived again from the new amounts, so an old LTV/CLTV
// cannot stay behind. On a purchase the appraised value follows the new
// property value unless the form had a separate appraisal: before 11-02 it
// kept the form's appraisal, so 500,000 -> 1,000,000 in the bar sent LTV 140%.
export function filterBarBody(searchParams, values) {
  const sp = searchParams || {};
  const isPurchase = sp.purpose === "Purchase";
  const program = sp.loanProgram;
  const secondLien = SECOND_LIEN.includes(program);
  const property = Number(values.propertyValue) || 0;
  const formProperty = Number(sp.propertyValue) || 0;
  const formAppraisal = Number(sp.appraisedValue) || 0;
  const separateAppraisal = isPurchase && formAppraisal > 0 && formAppraisal !== formProperty;
  const appraisal = separateAppraisal ? formAppraisal : property;
  const subject = program === "heloc"
    ? Number(sp.drawAmount) || 0
    : (secondLien
      ? Number(values.loanAmount) || 0
      : (isPurchase ? property - (Number(values.downPayment) || 0) : Number(values.loanAmount) || 0));
  const collateral = getCollateralValue({ loanPurpose: sp.purpose, propertyValue: property, appraisedValue: appraisal });
  const metrics = collateral > 0
    ? calculateLoanMetrics({
      loanProgram: program,
      loanPurpose: sp.purpose,
      propertyValue: property,
      appraisedValue: appraisal,
      loanAmount: subject,
      secondaryLoanAmount: sp.secondaryLoanAmount,
      primaryMortgageAmount: sp.primaryMortgageAmount,
      lineAmount: sp.lineAmount,
      drawAmount: sp.drawAmount,
    })
    : {};
  const target = values.searchBy
    ? {
      searchBy: values.searchBy,
      targetPrice: values.targetPrice ?? sp.targetPrice,
      targetRate: values.searchBy === "rate" ? values.targetRate : undefined,
      targetPriceBasis: values.targetPriceBasis === undefined ? sp.targetPriceBasis : targetPriceBasisOf(values),
    }
    : {};
  return {
    ...sp,
    ...target,
    compensationType: values.compType,
    propertyValue: property,
    appraisedValue: appraisal,
    downPayment: !secondLien && isPurchase ? Number(values.downPayment) || 0 : undefined,
    loanAmount: subject,
    totalLoanAmount: secondLien ? undefined : subject + (Number(sp.secondaryLoanAmount) || 0),
    secondaryLoanAmount: secondLien ? undefined : sp.secondaryLoanAmount,
    LTV: metrics.ltv,
    CLTV: metrics.cltv,
    HCLTV: metrics.hcltv,
    cltvInput: metrics.cltv,
    hcltvInput: metrics.hcltv,
  };
}

// --- one response for several terms (work package 2) ------------------------

const pricedTerm = (item) => item && item.pricedAs && item.pricedAs.loanTerm;

// A response priced with loanTerms carries pricedAs on every product and
// reason; split it into one response per term so the rest of the page works
// the same way as with one request per term.
export function splitResponseByTerm(response, terms) {
  const resp = response || {};
  const byTerm = {};
  terms.forEach((term) => {
    const combination = (resp.combinations || []).find((c) => c && c.loanTerm === term);
    const result = (resp.result || [])
      .map((lender) => ({
        ...lender,
        ProductRatesCosts: (lender.ProductRatesCosts || []).filter((p) => !pricedTerm(p) || pricedTerm(p) === term),
      }))
      .filter((lender) => lender.ProductRatesCosts.length > 0);
    const ineligible = {};
    Object.entries(resp.ineligible_reasons || {}).forEach(([lender, raw]) => {
      const entry = { ...raw };
      entry.ProductLevelReasons = (raw.ProductLevelReasons || []).filter((p) => !pricedTerm(p) || pricedTerm(p) === term);
      if (Array.isArray(raw.lenderLevelReasons)) {
        const own = raw.lenderLevelReasons.find((r) => pricedTerm(r) === term);
        entry.LenderLevelReason = own ? own.reason || "" : "";
        entry.lenderLevelReasonCode = own ? own.reasonCode : undefined;
        entry.lenderLevelMessage = own ? own.message : undefined;
      }
      delete entry.lenderLevelReasons;
      ineligible[lender] = entry;
    });
    const status = combination
      ? combination.status
      : (result.length > 0 ? "Ok" : resp.status);
    byTerm[term] = { ...resp, status, result, ineligible_reasons: ineligible };
    delete byTerm[term].combinations;
  });
  return byTerm;
}

// --- products ----------------------------------------------------------------

export function mergedConnectedMap(quotes) {
  const out = {};
  Object.values(quotes || {}).forEach((quote) => {
    const map = (quote && quote.response && quote.response.connected_lender_map) || {};
    Object.entries(map).forEach(([lender, connected]) => {
      out[lender] = Boolean(out[lender] || connected);
    });
  });
  return out;
}

export function lenderDisplayName(lender, response) {
  const names = (response && response.lender_display_names) || {};
  return names[lender] || com.formatLenderDisplayName(lender);
}

// One rate of a product, with the numbers every column and the rate detail use.
// Prices are on the base-100 scale: above 100 is a credit to the borrower,
// below 100 a cost. Amounts are in dollars, positive = cost (the engine's
// lender_credit convention), so `finalAmount < 0` is a lender credit.
// costPrice: Cost to borrower on the price scale (costPriceOf), null when the
// rate has no closing_cost or there is no loan amount.
function makeRow(rate, product, lender, ctx) {
  const raw = {
    ...product.product,
    Lender: lender.Lender,
    FileInfo: lender.FileInfo,
    Aus: product.AUSEngine,
    ...rate,
    Adjustments: product.appliedAdjustments,
    loanAmount: ctx.loanAmount,
    isConnected: ctx.isConnected,
  };
  const gross = getMaxPriceBreakdown(raw);
  const net = getNetPricingBreakdown(raw);
  const prices = rate.prices || {};
  const lenderPaid = ctx.compensationType !== "borrower_paid";
  const compPoints = ctx.isConnected && lenderPaid ? Number(prices.compensationPoint) || 0 : null;
  return {
    rate: Number(rate.base_rate),
    apr: Number(rate.APR),
    price: gross.afterCapPrice,
    priceAmount: gross.afterCapAmount,
    comp: compPoints,
    compAmount: compPoints === null ? null : compPoints * ctx.loanAmount / 100,
    final: net.price,
    finalAmount: net.amount,
    closing: Number(rate.closing_cost) || 0,
    costPrice: costPriceOf(rate.closing_cost, ctx.loanAmount),
    pmt: Number(rate.monthly_payment) || 0,
    dti: Number.isFinite(Number(rate.DTI)) ? Number(rate.DTI) : null,
    capped: gross.hasMaxPriceCap && gross.beforeCapPoints < gross.capPoints,
    capPrice: gross.hasMaxPriceCap ? 100 - gross.capPoints : null,
    raw,
  };
}

// Cost to borrower as a base-100 price, the scale a cost-to-borrower target
// is typed on: 100 - cost / loan amount x 100 ($0 is 100, 1% of the loan is
// 99, a net credit is above 100). The calculator's targetPriceBasis
// costToBorrower divides by the same subject loan amount (baseLoanAmount; a
// HELOC's draw, or its line when nothing is drawn).
export function costPriceOf(closingCost, loanAmount) {
  if (closingCost === undefined || closingCost === null || closingCost === "") return null;
  const cost = Number(closingCost);
  const amount = Number(loanAmount);
  if (!Number.isFinite(cost) || !(amount > 0)) return null;
  return 100 - cost / amount * 100;
}

const byRateThenPrice = (a, b) => a.rate - b.rate || b.price - a.price;

// ctx: { loanAmount, lockDays, compensationType, customerid }
export function buildProducts(quotes, ctx = {}) {
  const connected = mergedConnectedMap(quotes);
  const products = [];
  Object.entries(quotes || {}).forEach(([term, quote]) => {
    const response = quote && quote.response;
    if (!response) return;
    (response.result || []).forEach((lender) => {
      const isConnected = Boolean(connected[lender.Lender]);
      const sheetDate = lender.rateSheetDate || rateSheetDate(lender.FileInfo);
      (lender.ProductRatesCosts || []).forEach((product, j) => {
        const p = product.product || {};
        // A customer-specific rule the page has always applied.
        if (ctx.customerid === "siliconcitymortgage" && String(p.Mtype || "").includes("FHA")) return;
        const rows = (product.rateCosts || [])
          .filter((rate) => !ctx.lockDays || Number(rate.lockDay) === Number(ctx.lockDays))
          .map((rate) => makeRow(rate, product, lender, { ...ctx, loanAmount: Number(ctx.loanAmount) || 0, isConnected }))
          .filter((row) => Number.isFinite(row.rate))
          .sort(byRateThenPrice);
        if (rows.length === 0) return;
        products.push({
          id: `${term}|${lender.Lender}|${j}|${p.Name}`,
          term,
          lender: lender.Lender,
          lenderName: lender.lenderDisplayName || lenderDisplayName(lender.Lender, response),
          name: p.Name || "",
          mtype: p.Mtype || "",
          arm: p.Arm || null,
          aus: product.AUSEngine || [],
          adjustments: product.appliedAdjustments || {},
          fileInfo: lender.FileInfo || null,
          rateSheetDate: sheetDate,
          staleRateSheet: isRateSheetStale(sheetDate, ctx.today),
          isConnected,
          maxDTI: Number.isFinite(Number(p.maxDTI)) && p.maxDTI !== null ? Number(p.maxDTI) : null,
          dtiChecked: p.dtiChecked,
          rows,
        });
      });
    });
  });
  return products;
}

// The target the page matches each product's rates against (11-04): a price
// or a rate. A price is on the Price column's scale: lock-day price plus the
// adjustments, after the lender's maximum-price cap, compensation NOT
// included (the user, 11-04: "target price 不要 Comp"); with basis
// costToBorrower it is compared with the Cost to borrower on the same scale
// (costPriceOf). A bare number is a price, as before the rate target came
// back.
const RATE_EPSILON = 1e-6;
export function targetOf(target) {
  if (target && typeof target === "object") {
    const value = Number(target.value);
    if (target.by === "rate" && Number.isFinite(value) && value > 0) return { by: "rate", value };
    return {
      by: "price",
      value: target.by !== "rate" && Number.isFinite(value) ? value : 100,
      basis: target.basis === TARGET_BASIS_COST ? TARGET_BASIS_COST : TARGET_BASIS_PRICE,
    };
  }
  return { by: "price", value: Number.isFinite(Number(target)) ? Number(target) : 100, basis: TARGET_BASIS_PRICE };
}

const rateDistance = (row, goal) => Math.abs(row.rate - goal);

// The number a price target is compared with on each row: the Price, or with
// basis costToBorrower the Cost to borrower as a price (null where a rate has
// none). A product none of whose rates has a cost to borrower is matched on
// Price, so it still has a Target row.
function targetPrices(rows, goal) {
  if (goal.basis !== TARGET_BASIS_COST) return rows.map((row) => row.price);
  const costs = rows.map((row) => (Number.isFinite(row.costPrice) ? row.costPrice : null));
  return costs.some((value) => value !== null) ? costs : rows.map((row) => row.price);
}

// Rows are rate low to high. By price (D25, now on Price instead of Final
// price): the lowest rate whose Price (or Cost to borrower as a price,
// targetPrices) reaches the target; when no rate does, the closest one, that
// is the highest. By rate: that rate when the product has it; otherwise the
// closest rate, the lower one when two are as close.
export function pickTargetIndex(rows, target) {
  if (!rows || rows.length === 0) return -1;
  const goal = targetOf(target);
  if (goal.by === "rate") {
    return rows.reduce((best, row, k) => {
      const d = rateDistance(row, goal.value) - rateDistance(rows[best], goal.value);
      if (d < -RATE_EPSILON) return k;
      if (d > RATE_EPSILON) return best;
      if (row.rate < rows[best].rate - RATE_EPSILON) return k;
      if (Math.abs(row.rate - rows[best].rate) <= RATE_EPSILON && row.price > rows[best].price) return k;
      return best;
    }, 0);
  }
  const prices = targetPrices(rows, goal);
  const i = prices.findIndex((price) => price !== null && price >= goal.value - 1e-9);
  if (i >= 0) return i;
  return prices.reduce((best, price, k) => (price !== null && (prices[best] === null || price > prices[best]) ? k : best), 0);
}

export const targetRow = (product, target) => product.rows[pickTargetIndex(product.rows, target)];

const SORT_VALUE = {
  // Lender and Program are two columns since 11-04; each sorts by its own
  // name, the other name breaks ties.
  lender: (p) => `${p.lenderName}\u0000${p.name}`.toLowerCase(),
  program: (p) => `${p.name}\u0000${p.lenderName}`.toLowerCase(),
  rate: (p, row) => row.rate,
  apr: (p, row) => row.apr,
  price: (p, row) => row.price,
  comp: (p, row) => (row.comp === null ? -Infinity : row.comp),
  final: (p, row) => row.final,
  closing: (p, row) => row.closing,
  pmt: (p, row) => row.pmt,
  dti: (p, row) => (Number.isFinite(row.dti) ? row.dti : Infinity),
};

// By price: the row's Price (or Cost to borrower as a price) reaches the
// target. By rate: the product has exactly that rate.
export function reachesTarget(row, target) {
  const goal = targetOf(target);
  if (goal.by === "rate") return rateDistance(row, goal.value) <= RATE_EPSILON;
  const price = targetPrices([row], goal)[0];
  return price !== null && price >= goal.value - 1e-9;
}

// The order without a clicked column, after "reaches the target first".
// By price (D29): rate low to high and, at the same rate, the higher Price
// first. By rate (11-04): closest to the target rate first (the products that
// have it, then the nearest), and at the same distance the higher Price, then
// the lower rate.
function defaultOrder(a, b, goal) {
  if (goal.by === "rate") {
    const d = rateDistance(a.row, goal.value) - rateDistance(b.row, goal.value);
    if (Math.abs(d) > RATE_EPSILON) return d;
    return b.row.price - a.row.price || a.row.rate - b.row.rate;
  }
  return a.row.rate - b.row.rate || b.row.price - a.row.price;
}

// Inside a term, products that reach the target come first (by price: whose
// best Price is still below the target, capped by the lender's maximum price
// for example, come after; by rate: the ones without that exact rate come
// after), each part in defaultOrder. A clicked column sorts by that column
// instead. Both use the rate at the target, never the rate picked in the rates
// table, so picking another rate does not move the row.
export function orderProducts(products, target, sort) {
  const goal = targetOf(target);
  const withRow = products.map((p) => {
    const row = targetRow(p, goal);
    return { p, row, reaches: reachesTarget(row, goal) };
  });
  const fn = sort && SORT_VALUE[sort.key];
  const dir = sort && sort.order === "descend" ? -1 : 1;
  withRow.sort((a, b) => {
    if (!fn && a.reaches !== b.reaches) return a.reaches ? -1 : 1;
    if (fn) {
      const va = fn(a.p, a.row);
      const vb = fn(b.p, b.row);
      if (va < vb) return -dir;
      if (va > vb) return dir;
    }
    return defaultOrder(a, b, goal)
      || a.p.lenderName.localeCompare(b.p.lenderName)
      || a.p.name.localeCompare(b.p.name);
  });
  return withRow.map((x) => x.p);
}

export function visibleProducts(products, { terms, lenders } = {}) {
  const termSet = terms ? new Set(terms) : null;
  const lenderSet = lenders ? new Set(lenders) : null;
  return products.filter((p) => (!termSet || termSet.has(p.term)) && (!lenderSet || lenderSet.has(p.lender)));
}

// One group per selected term, in term order. `quotes` tells which terms are
// still pricing or failed, so a group can say so instead of disappearing.
export function groupByTerm(products, { terms, target, sort, quotes = {} }) {
  return sortTerms(terms).map((term) => {
    const own = orderProducts(products.filter((p) => p.term === term), target, sort);
    const atTarget = own.map((p) => targetRow(p, target));
    const reaching = atTarget.filter((row) => reachesTarget(row, target));
    const rates = (reaching.length ? reaching : atTarget).map((row) => row.rate);
    const quote = quotes[term] || {};
    return {
      term,
      label: termLabel(term),
      products: own,
      lowestRate: rates.length ? Math.min(...rates) : null,
      pending: quote.status === "loading",
      error: quote.status === "error" ? quote.error : null,
    };
  });
}

export function countBy(products, key) {
  const out = {};
  products.forEach((p) => {
    out[p[key]] = (out[p[key]] || 0) + 1;
  });
  return out;
}

// The lenders the Lenders filter offers: everyone with a priced product,
// connected first, then by name.
export function lenderOptions(products) {
  const seen = new Map();
  products.forEach((p) => {
    if (!seen.has(p.lender)) seen.set(p.lender, { value: p.lender, label: p.lenderName, myLenders: p.isConnected });
  });
  return [...seen.values()].sort((a, b) => (b.myLenders - a.myLenders) || a.label.localeCompare(b.label));
}

// --- money -------------------------------------------------------------------

const dollars = (value) => `$${Math.round(Math.abs(value)).toLocaleString("en-US")}`;

// "credit $6,839" / "cost $2,625" (amount positive = cost).
export function creditCost(amount) {
  const value = Number(amount) || 0;
  if (Math.round(value) === 0) return { kind: "par", text: "par" };
  return value < 0
    ? { kind: "credit", text: `credit ${dollars(value)}` }
    : { kind: "cost", text: `cost ${dollars(value)}` };
}

export const formatRate = (value) => `${Number(value).toFixed(3)}%`;
export const formatPrice = (value) => Number(value).toFixed(3);
const minus = (value) => (value < 0 ? "−" : "");
export const formatMoney = (value) => {
  const v = Math.round(Number(value) || 0);
  return `${minus(v)}$${Math.abs(v).toLocaleString("en-US")}`;
};
export const formatMoney2 = (value) => {
  const v = Number(value) || 0;
  const text = Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${minus(v) && text !== "0.00" ? "−" : ""}$${text}`;
};

// "DTI 36.2% / 50% max" when income was entered and the product's DTI was
// checked (work package 1's dtiChecked/maxDTI). Nothing when income is blank:
// the user ruled out "income needed" lines (11-02).
export function dtiText(product, row, hasIncome) {
  if (!hasIncome || product.dtiChecked === false || row.dti === null || row.dti === undefined) return null;
  const over = product.maxDTI !== null && row.dti > product.maxDTI + 1e-9;
  const limit = product.maxDTI !== null ? ` / ${Number(product.maxDTI.toFixed(1))}% max` : "";
  return { text: `DTI ${row.dti.toFixed(1)}%${limit}`, over };
}

// The DTI line split in two, so a narrow table can drop the limit and keep
// the number: { value: "36.2%", limit: "50% max" | null, over }.
export function dtiParts(product, row, hasIncome) {
  const dti = dtiText(product, row, hasIncome);
  if (!dti) return null;
  const limit = product.maxDTI !== null ? `${Number(product.maxDTI.toFixed(1))}% max` : null;
  return { value: `${row.dti.toFixed(1)}%`, limit, over: dti.over };
}

// Some lenders end a product's name with its term ("PRIME CONFORMING FIXED
// (DU & LP): 30 YEAR FIXED"); the term is already the group (or the only
// ticked term), so the table drops that suffix. Only an exact term match
// after the last colon is dropped: "Agency FNMA:30/25 Yr Fixed" stays.
const termWords = (text) => String(text || "").toLowerCase().replace(/\b(yrs?|years?)\b/g, "year").replace(/[^a-z0-9/]+/g, " ").trim();
export function displayProductName(name, term) {
  const text = String(name || "");
  const at = text.lastIndexOf(":");
  if (at <= 0) return text;
  const head = text.slice(0, at).trim();
  if (!head || termWords(text.slice(at + 1)) !== termWords(termLabel(term))) return text;
  return head;
}

// --- closing cost (rate detail) ----------------------------------------------

const SECTION_KEYS = [
  ["A", "Origination charges", "originationCharges"],
  ["B", "Services you cannot shop for", "canNotShopServices"],
  ["C", "Services you can shop for", "canShopServices"],
  ["E", "Taxes and government fees", "taxesAndOtherGovernmentFees"],
  ["F", "Prepaids", "prepaids"],
  ["G", "Initial escrow payment at closing", "initialEscrowPaymentsAtClosing"],
  ["H", "Other", "other"],
];
const POINTS_ITEM = /\(points\)\s*$|^lender credits?\b/i;

// The same arithmetic as today's "Closing cost details" (A..J), except that
// this rate's points or credit is its own line instead of being folded into
// A or into the J formula: it is the one line that changes with the rate.
export function closingCostSummary(row, appliedDetail, { downPayment, purpose } = {}) {
  const raw = (row && row.raw) || {};
  const own = raw.closing_costs_detail || (raw.estimatedclosingcosts && raw.estimatedclosingcosts.closingCostsDetail) || {};
  const extra = appliedDetail || {};
  const valid = (item) => item && typeof item.serviceName === "string" && item.serviceName.trim() !== "";
  const sections = SECTION_KEYS.map(([letter, label, field]) => {
    const items = [...(Array.isArray(own[field]) ? own[field] : []), ...(Array.isArray(extra[field]) ? extra[field] : [])]
      .filter(valid)
      .filter((item) => !(field === "originationCharges" && POINTS_ITEM.test(item.serviceName)));
    const total = items.reduce((sum, item) => sum + (parseFloat(item.estimateAmount) || 0), 0);
    return { letter, label, field, items, total };
  });
  const sum = (letters) => sections.filter((s) => letters.includes(s.letter)).reduce((t, s) => t + s.total, 0);
  const loanCosts = sum("ABC");
  const otherCosts = sum("EFGH");
  // Points (lender_credit > 0): the calculator rounds them to three decimals
  // and charges the "x.xxx% of Loan Amount (Points)" line, so use that line's
  // amount; lender_credit can be a few dollars off it (Glory 5.375%: 742.40
  // vs 744.00) and the total would then miss the table's closing cost. A
  // credit (lender_credit < 0) is never in A and is added as is.
  const lenderCredit = Number(raw.lender_credit) || 0;
  const pointsLines = (Array.isArray(own.originationCharges) ? own.originationCharges : [])
    .filter((item) => valid(item) && /\(points\)\s*$/i.test(item.serviceName));
  // No points line at all (points rounded to 0.000%) means none were charged.
  const points = lenderCredit > 0 && Array.isArray(own.originationCharges)
    ? pointsLines.reduce((total, item) => total + (parseFloat(item.estimateAmount) || 0), 0)
    : lenderCredit;
  const totalClosingCost = loanCosts + otherCosts + points;
  const mip = ((own.canNotShopServices || []).find((item) => item && item.serviceName === "Upfront Mortgage Insurance Premium") || {}).estimateAmount || 0;
  const down = purpose === "Purchase" ? Number(downPayment) || 0 : 0;
  return {
    sections,
    loanCosts,
    otherCosts,
    points,
    totalClosingCost,
    cashToClose: down - (Number(mip) || 0) + totalClosingCost,
    downPayment: down,
  };
}

// --- ineligibles ---------------------------------------------------------------

// Every searched term's ineligible list, merged per lender. Each reason keeps
// the terms it was given for, so a multi-term search can say "30 Year Fixed,
// 7/6 ARM" next to it. Lenders with an eligible product in a term are still
// listed for the other terms' reasons.
export function mergeIneligible(quotes, scenario = {}) {
  const merged = {};
  const order = [];
  // The calculator's connected_lender_map can leave out a connected lender
  // in one term's answer (dev, 11-02: AAA missing from a 5/6 ARM answer that
  // carries AAA's reason), so every term uses the map of all terms.
  const connected = mergedConnectedMap(quotes);
  sortTerms(Object.keys(quotes || {})).forEach((term) => {
    const response = quotes[term] && quotes[term].response;
    if (!response) return;
    const byLender = buildIneligibleByLender({ ...response, connected_lender_map: connected }, { ...scenario, loanTerm: term });
    Object.entries(byLender).forEach(([lender, entry]) => {
      if (!merged[lender]) {
        merged[lender] = { Lender: lender, lenderName: lenderDisplayName(lender, response), groups: [], byKey: {} };
        order.push(lender);
      }
      const target = merged[lender];
      const add = (reason, category, productName, isLender) => {
        const key = `${isLender ? "L" : "P"}|${reason}`;
        if (!target.byKey[key]) {
          target.byKey[key] = { reason, category, isLender, terms: [], products: [] };
          target.groups.push(target.byKey[key]);
        }
        const group = target.byKey[key];
        if (!group.terms.includes(term)) group.terms.push(term);
        if (productName && !group.products.includes(productName)) group.products.push(productName);
      };
      if (entry.LenderLevelReason) add(entry.LenderLevelReason, entry.LenderLevelCategory, null, true);
      entry.ProductLevelReasons.forEach((p) => add(p.IneligibleReason, p.category, p.ProductName, false));
    });
  });
  return order.map((lender) => {
    const { byKey, ...rest } = merged[lender];
    const products = new Set();
    rest.groups.forEach((g) => g.products.forEach((name) => products.add(name)));
    return { ...rest, productCount: products.size };
  });
}

// What the Ineligibles tab lists for the current filters: reasons of the
// ticked terms only; a lender unticked in the Lenders filter is hidden (the
// filter only offers lenders with a priced product, so a connected lender
// with nothing priced always stays).
export function visibleIneligible(ineligible, { terms, lenders, products = [] } = {}) {
  const termSet = terms ? new Set(terms) : null;
  const offered = new Set(products.map((p) => p.lender));
  const chosen = lenders ? new Set(lenders) : null;
  return (ineligible || [])
    .filter((entry) => !chosen || !offered.has(entry.Lender) || chosen.has(entry.Lender))
    .map((entry) => {
      const groups = entry.groups
        .map((g) => ({ ...g, terms: termSet ? g.terms.filter((t) => termSet.has(t)) : g.terms }))
        .filter((g) => g.terms.length > 0);
      const names = new Set();
      groups.forEach((g) => g.products.forEach((name) => names.add(name)));
      return { ...entry, groups, productCount: names.size };
    })
    .filter((entry) => entry.groups.length > 0);
}

// The one banner the page keeps (11-02): when no eligible product is shown.
export function zeroSentence(quotes, ineligible, scenario = {}) {
  if (!ineligible || ineligible.length === 0) return "";
  const terms = sortTerms(Object.keys(quotes || {}).filter((t) => quotes[t] && quotes[t].response));
  if (terms.length === 1) {
    const response = quotes[terms[0]].response;
    return zeroResultSentence(response, buildIneligibleByLender(response, { ...scenario, loanTerm: terms[0] }), { ...scenario, loanTerm: terms[0] });
  }
  // Several terms: the same sentence over the merged list.
  const asWp0 = {};
  ineligible.forEach((entry) => {
    const lenderGroups = entry.groups.filter((g) => g.isLender);
    const productGroups = entry.groups.filter((g) => !g.isLender);
    asWp0[entry.Lender] = {
      LenderLevelReason: lenderGroups.length ? lenderGroups[0].reason : "",
      LenderLevelCategory: lenderGroups.length ? lenderGroups[0].category : "",
      ProductLevelReasons: productGroups.flatMap((g) => g.products.length ? g.products.map(() => ({ category: g.category })) : [{ category: g.category }]),
    };
  });
  return zeroResultSentence({}, asWp0, { ...scenario, loanTerm: undefined });
}

// Where a search is (11-04): `loading` shows the spinner instead of the table;
// `busy` keeps the table and shows "Updating…"; `settled` means every term
// has answered, the only time "No qualified products" may be said. The
// spinner stays up while nothing eligible has arrived and any term is still
// pricing -- a term that failed fast (an unsupported ARM term, say) or came
// back empty is not the answer yet -- and on the first render of a new search,
// before its requests start (started: the form has a search to price).
export function quotePhase(termStates, somethingEligible, started) {
  const states = termStates || [];
  const anyLoading = states.some((q) => q && q.status === "loading");
  const notStarted = states.length === 0 && Boolean(started);
  const loading = notStarted || (anyLoading && !somethingEligible);
  return { loading, busy: !loading && anyLoading, settled: !notStarted && !anyLoading };
}
